import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import qs from 'qs'

import { SessionProvider } from '@shared/plugins/session-provider'
import { isProd } from '@shared/utils/constants/app-mode'
import { errorInterceptor } from '@shared/utils/helpers/error-guards/error-interceptor'
import { lsGet, LsGlobalKeys } from '@shared/utils/helpers/local-storage'

import { Landing } from '@protots/apps/pc/response/leads/landing'
import { PeerProps } from '@protots/auth/peer_props'
import { Session } from '@protots/auth/session'
import { SessionData } from '@protots/auth/session_data'

import { version } from '@/package.json'
import { getAuthSession } from '@pc/helpers/session'
import { PatientSessionRO } from '@pc/models/proto/ro/leads'
import { JsonValue } from '@protobuf-ts/runtime'

const userSessionDataProvider = (sessionData: SessionData) => {
  const session = Session.fromJson(
    (sessionData?.authSession || {}) as JsonValue
  )

  const peerProps = PeerProps.fromJson(
    (sessionData?.peerProps || {}) as JsonValue
  )

  return { session, peerProps }
}

const patientSessionDataProvider = (landingData: Landing) => {
  const landing = landingData
    ? PatientSessionRO.fromJson(landingData as JsonValue)
    : null

  const session = Session.create(
    getAuthSession(landing?.data?.landingState) ?? {}
  )
  const peerProps = PeerProps.create(landing?.data?.peerProps || {})

  return { session, peerProps }
}

const getProdSessionData = () => {
  const isPatient = Boolean(window.landing)

  return isPatient
    ? patientSessionDataProvider(window.landing)
    : userSessionDataProvider(window.sessionData)
}

export const getStoredSession = () => {
  const prodSession = getProdSessionData().session
  const existingSession = lsGet(LsGlobalKeys.Crd)
  const devSession = existingSession
    ? Session.fromJsonString(existingSession)
    : null

  const session = isProd ? prodSession : devSession

  return session
}

export const getCurrentUser = () => {
  const { user, impersonatedAs } = getStoredSession()!

  return impersonatedAs ?? user
}

export const httpService = axios.create({
  baseURL: SessionProvider.baseUrl(),
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    version,
  },
  // Needed for sending Array of Objects in URL params
  // Rails does not parse properly with the default params serializer
  // Default: x[]={"k":"v"}&x[]={"a": "b"}
  // With brackets: x[][k]=v&x[][a]=b
  // paramsSerializer: (params) =>
  //   qs.stringify(params, { arrayFormat: 'brackets' }),
  paramsSerializer: {
    serialize: (params: unknown) =>
      qs.stringify(params, { arrayFormat: 'brackets' }),
  },
})

export const resourcePath = '/api/apps'

httpService.interceptors.request.use((config) => {
  const { accessToken, deviceUuid } = SessionProvider.getStoredSession() ?? {}

  if (config?.namespace) {
    config.baseURL = `${config.baseURL}${resourcePath}/${config?.namespace}`
  }

  if (accessToken && deviceUuid) {
    config.headers!.authorization = `Bearer ${accessToken}`
    config.headers!['X-Fingerprint'] = deviceUuid
  }

  return config
})

httpService.interceptors.response.use(undefined, errorInterceptor)

export const get = (
  url: string,
  config: AxiosRequestConfig = {},
  service = httpService
) =>
  service.get<JsonValue, AxiosResponse<JsonValue, JsonValue>, JsonValue>(
    url,
    config
  )

export const post = (
  url: string,
  payload?: JsonValue,
  config: AxiosRequestConfig = {},
  service = httpService
) =>
  service.post<JsonValue, AxiosResponse<JsonValue, JsonValue>, JsonValue>(
    url,
    payload,
    config
  )

export const put = (
  url: string,
  payload: JsonValue,
  config: AxiosRequestConfig = {},
  service = httpService
) =>
  service.put<JsonValue, AxiosResponse<JsonValue, JsonValue>, JsonValue>(
    url,
    payload,
    config
  )

export const httpDelete = (
  url: string,
  config: AxiosRequestConfig = {},
  service = httpService
) =>
  service.delete<JsonValue, AxiosResponse<JsonValue, JsonValue>, JsonValue>(
    url,
    config
  )
